import axios from "axios";
import { AuthService } from "../../../data/services/AuthService";
import type { CompleteAuthUser } from "../../../stores/useAuthStore";
import { isCompleteUser } from "../../../lib/authUser";

// Delay before each retry; attempts = delays + 1.
export const CURRENT_USER_RETRY_DELAYS_MS = [500, 1000] as const;

export class CurrentUserLoadError extends Error {
  readonly cause: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "CurrentUserLoadError";
    this.cause = cause;
  }
}

export type ResolveCurrentUserOptions = {
  // Checked before every attempt — once the session has been cleared there
  // is nobody left to load a user for.
  isStale?: () => boolean;
};

/**
 * Only failures that can plausibly succeed on a second try are retried:
 * the request never got a response (network drop, timeout, blocked
 * request) or the server errored. A 401 has already been through the api
 * interceptor's refresh-and-retry, so reaching here means the session is
 * gone; any other 4xx won't change by asking again.
 */
function isRetryable(error: unknown): boolean {
  if (!axios.isAxiosError(error) || axios.isCancel(error)) return false;
  const status = error.response?.status;
  return status === undefined || status >= 500;
}

const wait = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Loads the signed-in user from GET /users/me. Identity comes only from
 * this endpoint — never from the access token's claims, which carry no
 * email or name and store the role under a key the app doesn't read.
 *
 * Resolves with a complete user, or throws CurrentUserLoadError.
 */
export async function resolveCurrentUser(
  options: ResolveCurrentUserOptions = {},
): Promise<CompleteAuthUser> {
  const { isStale } = options;
  let lastError: unknown;

  for (let attempt = 0; attempt <= CURRENT_USER_RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) await wait(CURRENT_USER_RETRY_DELAYS_MS[attempt - 1]);
    if (isStale?.()) {
      throw new CurrentUserLoadError("Session ended while loading the current user", lastError);
    }

    let user: unknown;
    try {
      user = await AuthService.fetchCurrentUser();
    } catch (error) {
      lastError = error;
      if (!isRetryable(error)) break;
      continue;
    }

    if (isCompleteUser(user)) return user;
    throw new CurrentUserLoadError("/users/me returned an incomplete user");
  }

  throw new CurrentUserLoadError("Could not load the current user", lastError);
}
